import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Heart, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useApp } from "@/context/AppContext";
import { useFavorites } from "@/context/FavoritesContext";
import { LANGS } from "@/i18n/translations";
import { scrollToId } from "@/lib/scroll";
import { toast } from "sonner";

export const Navbar = ({ solid = false }) => {
  const { t, lang, setLang } = useApp();
  const { favorites } = useFavorites();
  const [scrolledRaw, setScrolledRaw] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const scrolled = solid || scrolledRaw;

  useEffect(() => {
    const onScroll = () => setScrolledRaw(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const goAnchor = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 500);
    } else {
      scrollToId(id);
    }
  };

  const links = [
    { type: "route", to: "/stays", label: t("nav.search") },
    { type: "route", to: "/stays?sort=savings", label: t("nav.deals") },
    { type: "route", to: "/experiences", label: t("nav.experiences") },
    { type: "anchor", id: "zones", label: t("nav.zones") },
    { type: "anchor", id: "map", label: t("nav.map") },
    { type: "anchor", id: "why", label: t("nav.how") },
    { type: "route", to: "/alerts", label: t("nav.my_alerts") },
    { type: "route", to: "/bookings", label: t("nav.my_bookings") },
  ];

  const switchLang = (code) => {
    setLang(code);
    setLangOpen(false);
    const found = LANGS.find((l) => l.code === code);
    if (found) toast.success(`${found.flag} ${found.label}`);
  };

  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-sand-border py-2" : "py-4 bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 lg:px-8">
        <div className="shrink-0">
          <Logo light={!scrolled} />
        </div>

        <nav className="hidden items-center xl:flex xl:gap-4 2xl:gap-6">
          {links.map((l) =>
            l.type === "route" ? (
              <Link
                key={l.label}
                to={l.to}
                className={`whitespace-nowrap text-sm font-medium transition-colors hover:text-terracotta ${
                  scrolled ? "text-petrol" : "text-white/90"
                }`}
              >
                {l.label}
              </Link>
            ) : (
              <button
                key={l.label}
                onClick={() => goAnchor(l.id)}
                data-testid={`nav-anchor-${l.id}`}
                className={`whitespace-nowrap text-sm font-medium transition-colors hover:text-terracotta ${
                  scrolled ? "text-petrol" : "text-white/90"
                }`}
              >
                {l.label}
              </button>
            )
          )}
        </nav>

        <div className="flex shrink-0 items-center gap-3">
          <Link
            to="/compare"
            data-testid="nav-favorites"
            className={`relative flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
              scrolled ? "border-sand-border text-petrol hover:text-terracotta" : "border-white/30 text-white hover:text-terracotta"
            }`}
            aria-label={t("nav.favorites")}
          >
            <Heart size={17} />
            {favorites.length > 0 && (
              <span
                data-testid="nav-favorites-count"
                className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-none bg-terracotta px-1 text-[0.62rem] font-bold text-white"
              >
                {favorites.length}
              </span>
            )}
          </Link>

          <div ref={langRef} className="relative hidden sm:block">
            <button
              data-testid="lang-switcher-button"
              onClick={() => setLangOpen((o) => !o)}
              className={`flex items-center gap-1.5 rounded-none border px-3 py-1.5 text-sm font-medium transition-colors ${
                scrolled ? "border-sand-border text-petrol hover:border-terracotta" : "border-white/30 text-white hover:border-white"
              }`}
            >
              <span className="text-base leading-none">{current.flag}</span>
              <span className="hidden md:inline">{current.label}</span>
              <ChevronDown size={14} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div
                data-testid="lang-switcher-menu"
                className="absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-xl border border-sand-border bg-sand-card py-1 shadow-2xl"
              >
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`lang-switch-${l.code}`}
                    onClick={() => switchLang(l.code)}
                    className={`flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm transition-colors hover:bg-terracotta/10 ${
                      lang === l.code ? "font-semibold text-terracotta" : "text-petrol"
                    }`}
                  >
                    <span className="text-base leading-none">{l.flag}</span>
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            data-testid="nav-cta-compare"
            onClick={() => navigate("/stays")}
            className="hidden shrink-0 whitespace-nowrap rounded-none bg-terracotta px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-terracotta-hover md:block"
          >
            {t("nav.compare")}
          </button>

          <button
            className={`xl:hidden ${scrolled ? "text-petrol" : "text-white"}`}
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="mt-2 overflow-hidden border-t border-sand-border bg-sand shadow-xl xl:hidden"
        >
          <div className="flex flex-col gap-1 px-6 py-4">
            {links.map((l) =>
              l.type === "route" ? (
                <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="py-2 text-left font-medium text-petrol">
                  {l.label}
                </Link>
              ) : (
                <button key={l.label} onClick={() => goAnchor(l.id)} className="py-2 text-left font-medium text-petrol">
                  {l.label}
                </button>
              )
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-switch-mobile-${l.code}`}
                  onClick={() => switchLang(l.code)}
                  className={`flex items-center gap-1.5 rounded-none border px-3 py-1 text-xs font-semibold ${
                    lang === l.code ? "border-terracotta bg-terracotta text-white" : "border-sand-border text-petrol"
                  }`}
                >
                  <span className="text-sm leading-none">{l.flag}</span>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};
